import { useMemo, useState } from 'react';
import { VideocamOff } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  centerText,
  IDetectedBarcode,
  IScannerProps,
  Scanner as ScannerComp,
} from '@yudiel/react-qr-scanner';

import { CAMERA_PERMISSION_HELP_LINK } from '~common';
import { useUserAgent } from '~hooks';
import { CameraPermissionStatus, NotShowToastError } from '~types';
import { toastError } from '~utils/toast.util';

interface Props extends Omit<IScannerProps, 'onScan'> {
  onScanCallback?: (_code: string) => Promise<void>;
  onLoadingScannerErrorCallback?: (
    onErrorPermissionStatus: CameraPermissionStatus,
  ) => void;
}

const overlayStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
};

export const Scanner: React.FC<Props> = ({
  onScanCallback,
  onLoadingScannerErrorCallback,
  ...props
}) => {
  const [pause, setPause] = useState(false);
  const [isLoadingOnScanCallback, setLoadingOnScanCallback] = useState(false);

  const handleScan = async (detectedCode: IDetectedBarcode[]) => {
    try {
      const code = detectedCode[0].rawValue;
      if (code) {
        // pause scanner a little bit before processing
        setPause(true);

        if (onScanCallback) {
          setLoadingOnScanCallback(true);
          await onScanCallback(code); // Await the callback
        }
      }
    } catch (error) {
      // if the error is not an instance of NotShowToastError
      if (!(error instanceof NotShowToastError)) {
        toastError({ error, appMessage: 'This QR code is NOT valid.' });
      }

      setPause(true);
      setTimeout(() => {
        setPause(false); // allow re-scan after 1 seconds
      }, 1000);
    } finally {
      setLoadingOnScanCallback(false);
    }
  };

  const [cameraPermissionStatus, setCameraPermissionStatus] =
    useState<CameraPermissionStatus>(null);
  const { browser, deviceType } = useUserAgent();

  // Get the help link based on the browser and device type
  const helpLink = useMemo(
    () => CAMERA_PERMISSION_HELP_LINK[browser][deviceType] || '',
    [browser, deviceType],
  );

  return (
    <Box
      sx={{
        position: 'relative',
        '& svg': {
          display: 'none', // to remove default frame
        },
        '& video': {
          border: '3px solid #009EFA',
          borderRadius: '12px',
        },
        // image captured
        '& canvas': {
          border: '3px solid #009EFA',
          borderRadius: '12px',
          height: '100%',
        },
        ...(isLoadingOnScanCallback && {
          filter: 'blur(5px)', // Apply blur when loading
        }),
      }}
    >
      <ScannerComp
        {...props}
        formats={['qr_code', 'micro_qr_code', 'rm_qr_code']}
        allowMultiple={false}
        scanDelay={2000}
        paused={pause}
        onScan={handleScan} // Use the async handleScan
        onError={(error) => {
          let onErrorPermissionStatus: CameraPermissionStatus = null;
          // Handle specific error cases based on the error type
          if ((error as Error).name === 'NotAllowedError') {
            onErrorPermissionStatus = 'denied';
          } else {
            onErrorPermissionStatus = 'error';
          }

          setCameraPermissionStatus(onErrorPermissionStatus);
          onLoadingScannerErrorCallback?.(onErrorPermissionStatus);
        }}
        components={{
          audio: true,
          onOff: true,
          torch: true,
          zoom: true,
          finder: true,
          tracker: centerText,
        }}
        styles={{
          container: {
            // height should always same as weight (currently 100%) for better image
            minHeight: 'calc(100vw - 40px)', // padding x overall is 20px for each side
          },
        }}
      />
      {isLoadingOnScanCallback && (
        <Box sx={overlayStyle}>
          <CircularProgress />
        </Box>
      )}
      {cameraPermissionStatus && cameraPermissionStatus !== 'granted' && (
        <Box sx={{ ...overlayStyle, p: 2 }}>
          <VideocamOff
            color="error"
            fontSize="large"
            style={{ display: 'block' }}
          />
          <Typography variant="body2" color="error" textAlign="center">
            {cameraPermissionStatus === 'denied' && (
              <>
                Camera permissions have been denied.
                <br />
                Follow{' '}
                <a href={helpLink} target="_blank" rel="noopener noreferrer">
                  here
                </a>{' '}
                to learn how to enable camera access in your browser.
              </>
            )}
            {cameraPermissionStatus === 'error' &&
              'Something went wrong when loading the camera.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
